/**
 * Async folder delete (#781) — the shared vocabulary of the API, the storage
 * guard and the SPA. Dependency-free, like `write-freeze.ts`.
 */

/** Refusal code for a write into a folder whose delete job is running. */
export const FOLDER_BEING_DELETED_CODE = 'FOLDER_BEING_DELETED'

export function folderBeingDeletedMessage(): string {
  return 'This folder is being deleted. Wait for the delete to finish, or restore it from the bin afterwards.'
}

/** #823: what the AGENT is told when its write into a folder being deleted was refused at write-back. */
export function folderBeingDeletedSyncMessage(): string {
  return 'This file is in a folder that is being deleted, so your change was NOT saved to the drive. Do not retry it '
    + 'there: the folder is going to the bin. If the content is needed, write it to a location outside that folder.'
}

/**
 * Thrown by a write whose target lies in a folder that is being deleted.
 * Matched by NAME in the API error middleware (→ 409 FOLDER_BEING_DELETED),
 * so the class sets `this.name` explicitly — renaming it turns every refusal
 * into an opaque 500.
 */
export class FolderBeingDeletedError extends Error {
  /** #823: carried as a `code` too — the sync's error entry keeps only message + code, never the name */
  readonly code = FOLDER_BEING_DELETED_CODE
  constructor(public readonly folderId: string) {
    super(folderBeingDeletedMessage())
    this.name = 'FolderBeingDeletedError'
  }
}

/**
 * Folders of at least this many rows (the folder itself included) are deleted
 * by the background job when the client asks for it; smaller ones run inline.
 * Measured 2026-09-24: ~6,000 rows/s through `deleteToBin` locally, so 2,000
 * rows is well under a second inline even on a server 3-4x slower.
 * Env-overridable (`DELETE_ASYNC_MIN_NODES`), like EXTRACT_ASYNC_THRESHOLD_BYTES.
 */
export const DEFAULT_DELETE_ASYNC_MIN_NODES = 2_000

export function deleteAsyncMinNodes(
  env: Record<string, string | undefined> = typeof process !== 'undefined' && process.env ? process.env : {},
): number {
  const n = Number(env.DELETE_ASYNC_MIN_NODES)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : DEFAULT_DELETE_ASYNC_MIN_NODES
}

/** Refusal code for a folder too big to delete in one go. */
export const FOLDER_TOO_LARGE_TO_DELETE_CODE = 'FOLDER_TOO_LARGE_TO_DELETE'

/**
 * The most rows (the folder itself included) one delete may remove. Past it the
 * delete is refused before anything runs.
 *
 * Why a ceiling: `deleteToBin` holds the whole subtree's bin snapshot in memory
 * (about 1.6 KB per row as objects, then again as one JSON string) and removes
 * every row in one statement. Measured 2026-09-25 with the worker's 600 MB heap
 * cap: 107,692 rows peaked at 254 MB and took 28 s; 269,231 rows peaked at
 * 562 MB; 538,462 rows ran out of memory, which kills the worker and every job
 * on it. 100,000 keeps the heap under half the cap and the job well inside its
 * statement timeout on a server 3-4x slower than the benchmark machine.
 * Env-overridable (`DELETE_MAX_NODES`). Lifting it for real needs the snapshot
 * built outside Node (or #824, bin by re-parenting).
 */
export const DEFAULT_DELETE_MAX_NODES = 100_000

export function deleteMaxNodes(
  env: Record<string, string | undefined> = typeof process !== 'undefined' && process.env ? process.env : {},
): number {
  const n = Number(env.DELETE_MAX_NODES)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : DEFAULT_DELETE_MAX_NODES
}

/**
 * Refusal code for a delete or restore through a share link of a folder with
 * `deleteAsyncMinNodes()` or more rows (#854 D12). The drive runs those as a
 * background job; the share view does not (a link visitor may have no tenant
 * or user to key the job on), and inline they outrun Cloudflare's ~100 s edge
 * limit. So the share view refuses up front instead of failing after a timeout.
 */
export const TOO_LARGE_FOR_SHARE_VIEW_CODE = 'TOO_LARGE_FOR_SHARE_VIEW'

export function folderTooLargeToDeleteMessage(max: number): string {
  return `This folder has more than ${max.toLocaleString('en-US')} items, too many to delete at once. Delete some of its subfolders first.`
}

/**
 * Thrown when a folder holds more than `deleteMaxNodes()` rows. Matched by NAME
 * in the API error middleware (→ 409 FOLDER_TOO_LARGE_TO_DELETE).
 */
export class FolderTooLargeToDeleteError extends Error {
  constructor(public readonly max: number) {
    super(folderTooLargeToDeleteMessage(max))
    this.name = 'FolderTooLargeToDeleteError'
  }
}

/**
 * Per-statement time limit inside the delete JOB's transaction. Deployed boxes
 * give the app role 120 s (scripts/server-deploy.sh), which a delete near the
 * size ceiling can pass on a slow server; the job is off the request path, so
 * it may take longer. A statement past it is cancelled and the whole delete
 * rolls back. Env-overridable (`DELETE_JOB_STATEMENT_TIMEOUT_MS`).
 */
export const DEFAULT_DELETE_JOB_STATEMENT_TIMEOUT_MS = 5 * 60 * 1000

export function deleteJobStatementTimeoutMs(
  env: Record<string, string | undefined> = typeof process !== 'undefined' && process.env ? process.env : {},
): number {
  const n = Number(env.DELETE_JOB_STATEMENT_TIMEOUT_MS)
  return Number.isFinite(n) && n >= 1000 ? Math.floor(n) : DEFAULT_DELETE_JOB_STATEMENT_TIMEOUT_MS
}

/** What the person is told when the job's statement timeout cancelled the delete. */
/**
 * A delete refused because something inside the folder changed between the
 * delete's snapshot and its removal (an edit, a move, a re-pointed chain).
 * Nothing was deleted, and trying again is the answer.
 */
export function folderChangedDuringDeleteMessage(): string {
  return 'Something in this folder changed while it was being deleted, so nothing was deleted. Try again.'
}

export function folderDeleteTimedOutMessage(): string {
  return 'This folder took too long to delete, so nothing was deleted. Delete some of its subfolders first.'
}

/**
 * How long a folder's "being deleted" marker (`files.deleting_since`) is
 * honoured. Past this, every reader treats the folder as NOT being deleted
 * (writes are allowed again, a new delete may re-claim it), so a job lost
 * without clearing its marker can never strand a folder. A stale marker
 * blocking writes forever is worse than the race it guards. Measured deletes
 * take seconds to minutes; an hour is far past any real one.
 */
export const DELETE_MARKER_MAX_AGE_MS = 60 * 60 * 1000

/** Is a marker set at `since` still honoured? The ONE rule every reader uses. */
export function deletingMarkerIsLive(since: Date | string | null | undefined, now: number = Date.now()): boolean {
  if (since == null) return false
  const t = since instanceof Date ? since.getTime() : Date.parse(since)
  return Number.isFinite(t) && now - t < DELETE_MARKER_MAX_AGE_MS
}

// ── Big Bin restore as a background job (#854) ────────────────────────────
// Same thresholds as delete: a snapshot of deleteAsyncMinNodes() rows or more
// restores as a job when the client asks; one over deleteMaxNodes() is refused
// (the snapshot is parsed in memory, the profile that set the delete ceiling).

/** Refusal code: a Bin entry too big to restore in one go. */
export const FOLDER_TOO_LARGE_TO_RESTORE_CODE = 'FOLDER_TOO_LARGE_TO_RESTORE'

export function folderTooLargeToRestoreMessage(max: number): string {
  return `This folder has more than ${max.toLocaleString('en-US')} items, too many to restore at once.`
}

/** Refusal code: a background restore already holds this Bin entry. */
export const BIN_ITEM_BEING_RESTORED_CODE = 'BIN_ITEM_BEING_RESTORED'

export function binItemBeingRestoredMessage(): string {
  return 'This item is being restored. Wait for the restore to finish.'
}

/** A queued restore whose checks no longer pass when the job starts (D7). */
export function restoreAccessChangedMessage(): string {
  return 'Your access changed since you asked, so it was not restored. Try again.'
}

/**
 * A queued restore refused because uploads are paused (manual billing's write
 * freeze, D9). Says why a RESTORE is caught by an "uploads" pause: restoring
 * puts files back into the drive, which is what the pause blocks.
 */
export function restoreUploadsPausedMessage(): string {
  return 'It was not restored: this organization\'s payment needs to be settled first. Restoring puts files back in the drive, and adding files is paused until then.'
}

/**
 * A queued restore into a folder the person CHOSE, which by the time the job
 * starts is gone or no longer writable for them. The check answers both with one
 * refusal (so ids can't be probed), and so does this.
 */
export function restoreChosenFolderUnavailableMessage(): string {
  return 'The folder you chose is gone or you can no longer add to it, so it was not restored. Restore it again and choose another folder.'
}

/** A queued restore whose destination folder is gone by the time the job starts. */
export function restoreTargetGoneMessage(): string {
  return 'The folder it was going back to no longer exists, so it was not restored. Restore it again and choose where to put it.'
}

/** A queued relocation whose access change now needs a fresh confirmation. */
export function restoreNeedsConfirmationMessage(): string {
  return 'Restoring it there would now change who can see it, so it was not restored. Restore it again to confirm.'
}

// ─── "Delete forever" on one big Bin entry, as a background job (#1167 S2) ───

/** Refusal code: a "Delete forever" job already holds this Bin entry (someone else's). */
export const BIN_ITEM_BEING_DELETED_CODE = 'BIN_ITEM_BEING_DELETED'

export function binItemBeingDeletedMessage(): string {
  return 'This item is being permanently deleted. Wait for that to finish.'
}

/** A queued Delete forever whose checks no longer pass when the job starts. */
export function binPurgeAccessChangedMessage(): string {
  return 'Your access changed since you asked, so it was not deleted. Try again.'
}

/** A queued Delete forever whose entry left the Bin before the job ran (restored, or purged). */
export function binPurgeItemGoneMessage(): string {
  return 'This item is no longer in the Bin.'
}
